import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseResponse, Role, SignupUserInput, UserProfileData, UserType } from '../shared';
import { environment } from 'src/environments/environment';
import { map, tap, catchError } from 'rxjs';
import camelcaseKeys from 'camelcase-keys';

interface LoginResponse extends BaseResponse {
	token: string;
	email: string;
	verified: boolean;
	user_type: UserType | undefined;
	userTypeId: string;
	users_id: string;
	studios_id: string;
	guardians_id: string;
	competitions_id: string;
	user_fname: string;
	user_lname: string;
	user_phone: string;
	available_accounts: Role[];
	message: string;
}

interface CheckTokenResponse extends BaseResponse {
	date_verified: string;
	token: string;
	users_type_id: string;
	users_id: string;
	email: string;
	verified: boolean;
	user_type: UserType | undefined;
	studios_id: string;
	guardians_id: string;
	competitions_id: string;
	user_fname: string;
	user_lname: string;
	user_phone: string;
	available_accounts: Role[];
	message: string;
}

interface ExchangeTokenResponse extends BaseResponse {
	token: string;
	usersId: string;
}

interface ChangeEmailResponse extends BaseResponse {
	email: string;
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	constructor(private http: HttpClient) {}

	getToken() {
		const token = localStorage.getItem('dancebug_token');
		return token;
	}

	private setSession(token: string, usersId: string) {
		localStorage.setItem('dancebug_token', token);
		localStorage.setItem('dancebug_users_id', usersId);
	}

	login(username: string, password: string, deviceToken?: string, deviceId?: string) {
		const formData = new FormData();
		formData.append('email', username);
		formData.append('password', password);

		if (deviceId) {
			formData.append('device_id', deviceId);
		}

		if (deviceToken) {
			formData.append('firebase_token', deviceToken);
		}

		const request = this.http.post<LoginResponse>(`${environment.apiUrl}/login/`, formData);
		return request.pipe(
			map((res) => camelcaseKeys(res, { deep: true })),
			tap((data) => {
				if (!data.token) {
					throw Error('Failed to get authentication token from backend');
				}
				if (!data.usersId) {
					if (data.availableAccounts === undefined) throw Error('Failed to get available accounts from backend');
					if (!(Object.keys(data.availableAccounts).length > 1)) throw Error('Failed to get user id from backend');
				}

				this.setSession(data.token, data.usersId);
			})
		);
	}

	loginAs(userType: string) {
		const formData = new FormData();
		formData.append('userType', userType);

		const request = this.http.post<LoginResponse>(`${environment.apiUrl}/login/select-account/`, formData);
		return request.pipe(
			map((res) => camelcaseKeys(res, { deep: true })),
			tap((data) => {
				if (!data.token) {
					throw Error('Failed to get authentication token from backend');
				}
				if (!data.usersId) {
					throw Error('Failed to get user id from backend');
				}

				this.setSession(data.token, data.usersId);
			})
		);
	}

	signup(signupUserInput: SignupUserInput, reCaptchaToken: string) {
		const formData = new FormData();
		formData.append('username', signupUserInput.username);
		formData.append('username_verify', signupUserInput.username_verify);
		formData.append('password', signupUserInput.password);
		formData.append('password_verify', signupUserInput.password_verify);
		formData.append('first_name', signupUserInput.first_name);
		formData.append('last_name', signupUserInput.last_name);
		formData.append('phone', signupUserInput.phone);
		formData.append('userType', 'guardians');
		formData.append('recaptcha_response', reCaptchaToken);
		formData.append('login_now', '1');

		const request = this.http.post<LoginResponse>(`${environment.apiUrl}/users`, formData);
		return request.pipe(
			map((res) => camelcaseKeys(res, { deep: true })),
			tap((data) => {
				if (!data.token) {
					throw Error('Failed to get authentication token from backend');
				}
				if (!data.usersId) {
					throw Error('Failed to get user id from backend');
				}

				this.setSession(data.token, data.usersId);
			})
		);
	}

	checkToken() {
		const request = this.http.get<CheckTokenResponse>(`${environment.apiUrl}/verify-token`);
		return request.pipe(
			map((res) => camelcaseKeys(res, { deep: true })),
			catchError((err) => {
				throw err;
			})
		);
	}

	exchangeToken(token: string) {
		const formData = new FormData();
		formData.append('token', token);
		const request = this.http.post<ExchangeTokenResponse>(`${environment.apiUrl}/users/app-login-token`, formData);
		return request.pipe(
			map((res) => camelcaseKeys(res, { deep: true })),
			tap((data) => {
				if (!data.token) {
					throw Error('Failed to get authentication token from backend');
				}
				if (!data.usersId) {
					throw Error('Failed to get user id from backend');
				}

				this.setSession(data.token, data.usersId);
			}),
			catchError((err) => {
				throw err;
			})
		);
	}

	refreshToken() {
		return this.http.get<CheckTokenResponse>(`${environment.apiUrl}/login/token/1`).pipe(
			map((res) => camelcaseKeys(res, { deep: true })),
			tap((data) => {
				if (!data.token) {
					throw Error('Failed to get authentication token from backend');
				}
				if (!data.usersId) {
					throw Error('Failed to get user id from backend');
				}

				this.setSession(data.token, data.usersId);
			})
		);
	}

	forgotPassword(email: string) {
		const formData = new FormData();
		formData.append('email', email);

		const request = this.http.post<BaseResponse>(`${environment.apiUrl}/forgot-password/`, formData);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	resendEmailVerificationCode(usersId: string) {
		const request = this.http.put<BaseResponse>(`${environment.apiUrl}/users/${usersId}/verify-email`, null);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	changeVerificationEmail(usersId: string, email: string, verify_email: string) {
		const formData = new FormData();
		formData.append('email', email);
		formData.append('verify_email', verify_email);

		const request = this.http.post<ChangeEmailResponse>(`${environment.apiUrl}/users/${usersId}/verify-email`, formData);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	verifyEmail(code: string) {
		const request = this.http.put<BaseResponse>(`${environment.apiUrl}/users/verify-email/${code}`, null);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	updateUserProfile(usersId: string, data: Partial<UserProfileData>) {
		const formData = new FormData();
		if (data.first_name) {
			formData.append('user_fname', data.first_name);
			formData.append('fields_to_update[]', 'user_fname');
		}
		if (data.last_name) {
			formData.append('user_lname', data.last_name);
			formData.append('fields_to_update[]', 'user_lname');
		}
		if (data.phone) {
			formData.append('user_phone', data.phone);
			formData.append('fields_to_update[]', 'user_phone');
		}

		const request = this.http.post<BaseResponse>(`${environment.apiUrl}/users/${usersId}/user-information`, formData);
		return request.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	updateUserProfilePhoto(guardiansId: string, photo: File, offsetX: number, offsetY: number) {
		const formData = new FormData();

		if (photo !== undefined) {
			formData.append('imageFile', photo);
			formData.append('offsetX', `${offsetX}`);
			formData.append('offsetY', `${offsetY}`);
		}

		return this.http
			.post<BaseResponse>(`${environment.apiUrl}/guardians/${guardiansId}/update-profile-photo`, formData)
			.pipe(map((res) => camelcaseKeys(res, { deep: true })));
	}

	logoutUser(deviceId?: string) {
		// used to store User Lang so shouldn't be cleared fully
		// localStorage.clear();
		const formData = new FormData();

		if (deviceId) {
			formData.append('device_id', deviceId);
		}

		const request = this.http.post<BaseResponse>(`${environment.apiUrl}/logout`, formData);
		return request.pipe(
			map((res) => camelcaseKeys(res, { deep: true })),
			tap(() => {
				localStorage.removeItem('dancebug_token');
				localStorage.removeItem('dancebug_users_id');
			})
		);
	}
}
